/**
 * REGLERNA BAKOM TIDRAPPORTENS FYRA SKÄRMAR.
 *
 * De ligger samlade av samma skäl som app/datum.ts: skrivs de i varje skärm blir
 * tre av fyra fel, och den fjärde blir fel nästa gång någon rättar en av de tre.
 * Varje regel nedan pekar på BESLUTET eller KONTRAKTSRADEN den kommer ur.
 */

import {
  EDITABLE_WEEK_STATES,
  MANAGER_OWN_WEEK_WRITABLE_STATES,
  WORK_DATE_MAX_DAYS_AHEAD,
  WORK_DATE_MAX_DAYS_BACK,
  asMinutes,
  asWorkDate,
} from '@delat/types';
import { REPORTING_ROLES, WEEK_TRANSITIONS } from '@delat/routes';
import type {
  AbsenceKind,
  AbsenceRow,
  AccountId,
  CustomerWorkerRow,
  IsoWeek,
  Minutes,
  Role,
  Vehicle,
  WeekState,
  WorkDate,
  WorkOrderWorkerRow,
} from '@delat/types';
import type { Brickton, Dagston } from '../../ui';
import { idag, plusDagar, veckaFor } from '../../app/datum';
import type { Ogonblicksbild } from './lager';

/* == Veckan (beslut 04, 10 och 19) ======================================== */

/**
 * Veckans tillstånd för en person. En vecka utan rad är 'open': week_status
 * skapas först när något hänt i veckan (servern lägger den på köpet vid första
 * sparningen), och en tom vecka är öppen för skrivning.
 */
export const veckansLage = (
  bild: Ogonblicksbild,
  konto: AccountId,
  vecka: IsoWeek,
): WeekState =>
  bild.veckor.find((v) => v.account_id === konto && v.iso_week === vecka)?.state ?? 'open';

/**
 * FÅR DEN HÄR PERSONEN SKRIVA I DEN HÄR VECKAN?
 *
 * Normalt krävs EDITABLE_WEEK_STATES (open, submitted, reopened). BESLUT 10:s
 * andra halva ger en manager mer i SIN EGEN vecka — hans första sparning sätter
 * veckan till 'approved' direkt, och utan MANAGER_OWN_WEEK_WRITABLE_STATES vore
 * han utelåst ur sin egen vecka efter exakt en rad. Först 'locked' stänger
 * honom ute.
 *
 * Vidgningen till `readonly WeekState[]` är nödvändig: `as const satisfies` gör
 * listorna till sina egna smala typer, och includes() över dem går inte att
 * anropa med en WeekState. Samma grepp som worker/src/modules/sync/plan.ts.
 */
export const farSkrivaVecka = (args: {
  readonly lage: WeekState;
  /** Är det den inloggades EGEN vecka? */
  readonly egen: boolean;
  readonly arChef: boolean;
}): boolean => {
  const tillatna: readonly WeekState[] =
    args.arChef && args.egen ? MANAGER_OWN_WEEK_WRITABLE_STATES : EDITABLE_WEEK_STATES;
  return tillatna.includes(args.lage);
};

/** Knappen "Lämna in" härleds ur WEEK_TRANSITIONS, aldrig ur en gissning. */
export const kanLamnaIn = (lage: WeekState): boolean => {
  const fran: readonly WeekState[] = WEEK_TRANSITIONS.submit.from;
  return fran.includes(lage);
};

/**
 * Bokföraren finns inte i en enda rad i SYNC_RULES (beslut 07), så han SER
 * tidrapporten men har inga knappar. Vidgningen behövs av samma skäl som ovan:
 * REPORTING_ROLES är `readonly ReportingRole[]` och includes() över den kan inte
 * anropas med en Role.
 */
export const farRapportera = (roll: Role): boolean => {
  const rapporterande: readonly Role[] = REPORTING_ROLES;
  return rapporterande.includes(roll);
};

export const VECKOLAGETEXT: Readonly<Record<WeekState, string>> = {
  open: 'Öppen',
  submitted: 'Inlämnad',
  approved: 'Attesterad',
  locked: 'Låst',
  reopened: 'Öppnad igen',
};

/**
 * Brickans ton följer betydelsen och inte tillståndets namn: 'tilldelad'
 * betyder "något väntar på dig", vilket är precis vad en återöppnad vecka gör.
 */
export const VECKOLAGETON: Readonly<Record<WeekState, Brickton>> = {
  open: 'neutral',
  submitted: 'pagaende',
  approved: 'klar',
  locked: 'klar',
  reopened: 'tilldelad',
};

/* == Arbetsdagens fönster ================================================= */

/**
 * WORK_DATE_MAX_DAYS_BACK och _AHEAD är kontraktets — servern avvisar en
 * arbetsdag utanför fönstret med VALIDATION_FAILED { field: 'work_date' }.
 * Klienten sätter samma gränser på datumfältet så att montören får veta det
 * INNAN han sparat.
 */
export const tidigastArbetsdag = (nu: WorkDate = idag()): WorkDate =>
  plusDagar(nu, -WORK_DATE_MAX_DAYS_BACK);

export const senastArbetsdag = (nu: WorkDate = idag()): WorkDate =>
  plusDagar(nu, WORK_DATE_MAX_DAYS_AHEAD);

export const inomArbetsdagsfonstret = (dag: WorkDate, nu: WorkDate = idag()): boolean =>
  dag >= tidigastArbetsdag(nu) && dag <= senastArbetsdag(nu);

/**
 * VÄRDET UR ETT <input type="date"> SOM ARBETSDAG — prövat, aldrig märkt med ett
 * bart `as`.
 *
 * Tre skärmar (Tidny, Mil, Utlagg) skrev tidigare `text as WorkDate` rakt ur
 * fältet. Specen garanterar visserligen '' eller 'YYYY-MM-DD', så risken var
 * liten — men projektets egen regel i api/granska.ts är att en MÄRKT TYP aldrig
 * får uppstå ur ett bart as: "pröva formen först, märk sedan". Ett fält en
 * webbläsare fyller är användarindata som allt annat.
 *
 * null när fältet är tomt (webbläsaren lämnar tom sträng när det rensas) eller
 * när formen inte höll. Anroparen låter då dagen stå kvar — ett datumfält som
 * kastar mitt i en onChange hade tagit ner skärmen.
 */
export const arbetsdagUrFalt = (text: string): WorkDate | null => {
  if (text === '') return null;
  try {
    return asWorkDate(text);
  } catch {
    return null;
  }
};

/* == Momentkravet ========================================================= */

export type Momentkrav = 'kravs' | 'frivillig';

/**
 * KRAVET LIGGER PÅ MOMENTLISTAN, ALDRIG PÅ ETT FRITEXTFÄLT.
 *
 * Appboken har en hel panel om just det här, och den är facit: "Momentlista att
 * bocka i, inte ett fritextfält", "Kravet sätts per kundtyp: obligatoriskt mot
 * privat, frivilligt mot företag" — och skälen mot fritext, ordagrant: "Tvingar
 * man fram text skriver folk 'jobbat' eller 'wc'", "Att skriva på en telefon med
 * kalla händer är långsamt", "Fritext blir olika från person till person och går
 * inte att räkna på". Prototypens bricka heter därför "Privatkund — moment
 * krävs" och inte "anteckning krävs".
 *
 * KLIENTEN KRÄVDE TIDIGARE TRE TECKEN I EN ANTECKNING mot privatkund. Det släppte
 * igenom precis de ord appboken varnar för, och det var dessutom ett krav
 * KONTRAKTET inte känner: CompanySettingsRow bär bara mileage_ore_per_10km och
 * vat_rate_hundredths (shared/types.ts, schema/company.sql).
 *
 * VAD KRAVET BETYDER I DAG: en tidrad kan inte sparas utan moment överhuvudtaget
 * — TimeEntryWrite bär ett task_id, och skärmen kräver minst en ikryssad rad. Mot
 * en PRIVATKUND sägs det dessutom RAKT UT, för det är den kunden som läser
 * momenten på sin faktura. Anteckningen är frivillig i båda fallen och följer
 * med underlaget ut om den skrivs.
 *
 * NÄR company_settings får ett fält för kravet per firma är den här funktionen
 * det ENDA stället som ska ändras — brickan och hjälptexten på tidnyskärmen
 * läser båda härifrån. Att fältet saknas är en riktig lucka och den är
 * rapporterad.
 */
export const momentkravFor = (
  bild: Ogonblicksbild,
  order: WorkOrderWorkerRow | null,
): Momentkrav => {
  if (order === null) return 'frivillig';
  const kund = kundFor(bild, order);
  return kund?.kind === 'private' ? 'kravs' : 'frivillig';
};

/**
 * CHECK (note IS NULL OR length(note) BETWEEN 1 AND 2000) i schema/company.sql
 * sätter taket. Något GOLV finns inte längre: anteckningen är frivillig, och en
 * halvskriven mening är bättre än ingen alls.
 */
export const ANTECKNING_MAX_TECKEN = 2000;

/* == Uppslag i ögonblicksbilden =========================================== */

export const orderMedId = (
  bild: Ogonblicksbild,
  id: string | null,
): WorkOrderWorkerRow | null =>
  id === null ? null : (bild.ordrar.find((o) => o.id === id) ?? null);

export const kundFor = (
  bild: Ogonblicksbild,
  order: WorkOrderWorkerRow | null,
): CustomerWorkerRow | null =>
  order === null ? null : (bild.kunder.find((k) => k.id === order.customer_id) ?? null);

/**
 * SÅ HÄR KÄNNER EN HANTVERKARE IGEN ETT JOBB: adressen först, beskrivningen
 * sedan (prototypens Orderkort gör samma val). Saknar ordern adress får
 * beskrivningen bära den ensam.
 */
export const ordernamn = (order: WorkOrderWorkerRow | null): string =>
  order === null ? 'Okänd arbetsorder' : (order.address ?? order.description);

export const orderunder = (order: WorkOrderWorkerRow | null): string | undefined =>
  order === null || order.address === null ? undefined : order.description;

/** Aktiva ordrar först, sedan de avslutade; inom varje grupp efter adress. */
export const sorteradeOrdrar = (
  bild: Ogonblicksbild,
): readonly WorkOrderWorkerRow[] =>
  [...bild.ordrar].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
    return ordernamn(a).localeCompare(ordernamn(b), 'sv');
  });

export const momentMedId = (bild: Ogonblicksbild, id: string): string =>
  bild.moment.find((m) => m.id === id)?.name ?? 'Okänt moment';

/* == Dagen ================================================================ */

/**
 * En normal arbetsdag i minuter. ANVÄNDS BARA FÖR FÄRGEN på kalenderrutan —
 * ingen regel i kontraktet vet vad en full dag är, och ingenting sparas eller
 * avvisas på talet. Prototypen visar 8,5 h som 'ok' och 5,0 h som 'kort', vilket
 * är exakt den här gränsen.
 */
export const NORMAL_ARBETSDAG: Minutes = asMinutes(480);

export const minuterPaDag = (
  rader: readonly { readonly work_date: WorkDate; readonly minutes: Minutes }[],
  dag: WorkDate,
): Minutes =>
  asMinutes(
    rader.reduce((summa, rad) => (rad.work_date === dag ? summa + Number(rad.minutes) : summa), 0),
  );

export const franvaroPaDag = (
  franvaro: readonly AbsenceRow[],
  dag: WorkDate,
): AbsenceRow | null => franvaro.find((f) => f.work_date === dag) ?? null;

/**
 * TONEN PÅ EN DAG.
 *   'ok'      full dag rapporterad
 *   'kort'    något rapporterat, men mindre än en arbetsdag
 *   'saknas'  en passerad vardag utan rad och utan frånvaro
 *   'tom'     helg, frånvaro, eller en dag som inte kommit än
 */
export const dagenston = (args: {
  readonly minuter: Minutes;
  readonly franvarande: boolean;
  readonly helg: boolean;
  readonly framtid: boolean;
}): Dagston => {
  if (Number(args.minuter) >= Number(NORMAL_ARBETSDAG)) return 'ok';
  if (Number(args.minuter) > 0) return 'kort';
  if (args.franvarande || args.helg || args.framtid) return 'tom';
  return 'saknas';
};

export const veckanFor = (dag: WorkDate): IsoWeek => veckaFor(dag);

/* == Texter =============================================================== */

export const FORDONSTEXT: Readonly<Record<Vehicle, string>> = {
  own_car: 'egen bil',
  company_car: 'firmabil',
};

export const FRANVAROTEXT: Readonly<Record<AbsenceKind, string>> = {
  vacation: 'Semester',
  leave: 'Ledig',
  other: 'Annan frånvaro',
};

/** Kort form i kalenderrutan, där det bara får plats med några tecken. */
export const FRANVAROKORT: Readonly<Record<AbsenceKind, string>> = {
  vacation: 'Sem',
  leave: 'Led',
  other: 'Annat',
};
